import { Link } from "react-router-dom";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CalendarClock, Facebook, Instagram, Loader2, X, FileText,
  ChevronRight, Compass,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formatInTz, propertyTimezone, tzAbbreviation } from "@/lib/timezone";

/**
 * Format an ISO datetime as a friendly, property-tz-anchored string.
 * The `tz` argument comes from the active property's Settings so all
 * editors on a given property see the same wall-clock time regardless
 * of the browser they log in from. Fallback formatting preserves the
 * raw ISO if `Intl.DateTimeFormat` throws (e.g. an unrecognised zone).
 */
function formatWhen(iso, tz) {
  if (!iso) return "—";
  try {
    return formatInTz(iso, tz, "compact");
  } catch {
    return iso;
  }
}

/** Minutes-from-now for humans ("in 2h" / "in 3d" / "past due"). */
function formatRelative(iso) {
  if (!iso) return "";
  const now = Date.now();
  const then = new Date(iso).getTime();
  const diffMs = then - now;
  if (Number.isNaN(diffMs)) return "";
  if (diffMs < -60_000) return "past due";
  const abs = Math.abs(diffMs);
  const mins = Math.round(abs / 60_000);
  if (mins < 60) return `in ${mins} min`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `in ${hrs} h`;
  const days = Math.round(hrs / 24);
  return `in ${days} d`;
}

function KindBadge({ kind }) {
  const cfg = {
    wordpress: {
      label: "WORDPRESS",
      Icon: FileText,
      cls: "border-emerald-200 bg-emerald-50 text-emerald-800",
    },
    facebook: {
      label: "FACEBOOK",
      Icon: Facebook,
      cls: "border-sky-200 bg-sky-50 text-sky-800",
    },
    instagram: {
      label: "INSTAGRAM",
      Icon: Instagram,
      cls: "border-rose-200 bg-rose-50 text-rose-800",
    },
    research: {
      label: "RESEARCH",
      Icon: Compass,
      cls: "border-violet-200 bg-violet-50 text-violet-800",
    },
  }[kind] || {
    label: (kind || "").toUpperCase(),
    Icon: CalendarClock,
    cls: "border-neutral-200 bg-neutral-50 text-neutral-700",
  };
  const { Icon } = cfg;
  return (
    <span
      className={
        "inline-flex items-center gap-1 px-2 py-0.5 border text-[10px] font-mono-display tracking-wider " +
        cfg.cls
      }
      data-testid={`scheduled-task-kind-${kind}`}
    >
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
}

/**
 * "Scheduled Tasks" page — unified feed of every future-dated item on
 * the active property. WordPress future-posts + Facebook + Instagram
 * queued posts are merged and sorted earliest-first. Each row has an X
 * to cancel that specific task; cancelling a WP row flips the WP post
 * back to draft, cancelling a social row deletes the queued job.
 */
export default function ScheduledTasksPage() {
  const qc = useQueryClient();
  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: api.getSettings,
    staleTime: 5 * 60 * 1000,
  });
  const tz = propertyTimezone(settings);
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["scheduled-tasks"],
    queryFn: api.listScheduledTasks,
    refetchInterval: 30_000,  // stay reasonably fresh — scheduler ticks 30s
  });
  const items = data?.items || [];

  // Per-kind visibility toggle. Default: everything visible. The
  // filter is client-side only — no need to round-trip to the backend
  // when the payload is already ordered chronologically and typically
  // <100 items.
  const [visibleKinds, setVisibleKinds] = useState({
    wordpress: true,
    facebook: true,
    instagram: true,
    research: true,
  });
  const toggleKind = (k) =>
    setVisibleKinds((s) => ({ ...s, [k]: !s[k] }));

  const filteredItems = useMemo(
    () => items.filter((t) => visibleKinds[t.kind] !== false),
    [items, visibleKinds],
  );
  const counts = useMemo(() => {
    const c = { wordpress: 0, facebook: 0, instagram: 0, research: 0 };
    for (const t of items) if (c[t.kind] !== undefined) c[t.kind] += 1;
    return c;
  }, [items]);

  const cancel = useMutation({
    mutationFn: ({ kind, id }) => api.cancelScheduledTask(kind, id),
    onSuccess: (_res, vars) => {
      toast.success(
        vars.kind === "research"
          ? "Research schedule paused"
          : `Cancelled ${vars.kind} schedule`,
      );
      qc.invalidateQueries({ queryKey: ["scheduled-tasks"] });
      // Also invalidate downstream views that depend on this.
      qc.invalidateQueries({ queryKey: ["articles"] });
      qc.invalidateQueries({ queryKey: ["scheduled-posts"] });
      qc.invalidateQueries({ queryKey: ["research-schedules"] });
    },
    onError: (e, vars) => {
      toast.error(
        e?.response?.data?.detail ||
          `Failed to cancel ${vars.kind} schedule`,
      );
    },
  });

  return (
    <div className="flex-1 min-w-0 overflow-y-auto">
      <div className="max-w-6xl mx-auto px-4 sm:px-10 py-8">
      <div className="mb-8">
        <div className="overline mb-2">Newsroom · Queue</div>
        <h1 className="font-serif-display text-4xl sm:text-5xl tracking-tight">
          Scheduled Tasks
        </h1>
        <p className="mt-3 text-sm text-[var(--ink-muted)] max-w-2xl">
          Everything the newsdesk has queued for a future date &mdash; WordPress
          post-schedules, Facebook feed posts, Instagram feed posts, and
          recurring research runs. Sorted by
          the moment they&rsquo;re set to go live. Click <X className="w-3.5 h-3.5 inline-block align-text-bottom" /> to cancel any single item.
        </p>
        <p className="mt-2 text-[11px] font-mono-display text-[var(--ink-muted)]">
          All times shown in{" "}
          <span className="text-[var(--ink)]">{tz}</span> (
          {tzAbbreviation(tz)})
        </p>
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 text-sm text-[var(--ink-muted)] py-8">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading queue&hellip;
        </div>
      )}

      {isError && (
        <div className="text-sm px-4 py-3 border border-red-200 bg-red-50 text-red-800">
          Could not load scheduled tasks:{" "}
          {error?.response?.data?.detail || error?.message || "unknown error"}
        </div>
      )}

      {!isLoading && !isError && items.length === 0 && (
        <div
          data-testid="scheduled-tasks-empty"
          className="text-center border border-dashed border-[var(--border)] px-8 py-16 text-[var(--ink-muted)]"
        >
          <CalendarClock className="w-6 h-6 mx-auto mb-3 opacity-60" />
          <div className="font-serif-display text-lg mb-1">
            Nothing scheduled
          </div>
          <p className="text-sm max-w-md mx-auto">
            When you set a future publish date on WordPress or queue a
            Facebook / Instagram post, it will appear here so you can
            manage everything in one place.
          </p>
        </div>
      )}

      {!isLoading && !isError && items.length > 0 && (
        <div
          data-testid="scheduled-tasks-filters"
          className="mb-3 flex flex-wrap items-center gap-2 text-xs"
        >
          <span className="text-[10px] uppercase tracking-wider text-[var(--ink-muted)] font-mono-display mr-1">
            Show
          </span>
          {[
            { k: "wordpress", label: "WordPress", Icon: FileText,
              cls: "border-emerald-300 bg-emerald-50 text-emerald-800" },
            { k: "facebook",  label: "Facebook",  Icon: Facebook,
              cls: "border-sky-300 bg-sky-50 text-sky-800" },
            { k: "instagram", label: "Instagram", Icon: Instagram,
              cls: "border-rose-300 bg-rose-50 text-rose-800" },
            { k: "research",  label: "Research",  Icon: Compass,
              cls: "border-violet-300 bg-violet-50 text-violet-800" },
          ].map(({ k, label, Icon, cls }) => {
            const on = visibleKinds[k];
            return (
              <label
                key={k}
                data-testid={`filter-${k}`}
                className={
                  "inline-flex items-center gap-1.5 px-2.5 py-1 border cursor-pointer select-none " +
                  (on
                    ? cls
                    : "border-[var(--border)] bg-white text-[var(--ink-muted)] opacity-60")
                }
              >
                <input
                  type="checkbox"
                  checked={on}
                  onChange={() => toggleKind(k)}
                  className="w-3 h-3 accent-current"
                  data-testid={`filter-${k}-checkbox`}
                />
                <Icon className="w-3 h-3" />
                <span className="font-mono-display tracking-wider text-[10px] uppercase">
                  {label}
                </span>
                <span className="text-[10px] opacity-80">({counts[k] || 0})</span>
              </label>
            );
          })}
        </div>
      )}

      {!isLoading && !isError && items.length > 0
          && filteredItems.length === 0 && (
        <div
          data-testid="scheduled-tasks-empty-filtered"
          className="text-center border border-dashed border-[var(--border)] px-8 py-12 text-[var(--ink-muted)] text-sm"
        >
          No tasks match the current filter. Turn a checkbox above back
          on to see hidden items.
        </div>
      )}

      {!isLoading && filteredItems.length > 0 && (
        <ul
          data-testid="scheduled-tasks-list"
          className="divide-y divide-[var(--border)] border border-[var(--border)] bg-white"
        >
          {filteredItems.map((t) => {
            const isPending =
              cancel.isPending &&
              cancel.variables?.kind === t.kind &&
              cancel.variables?.id === t.id;
            // Research rows are recurring rules, not article jobs: they
            // have no `article_id`, so the row links back to the
            // Research tab (where the schedule is editable) instead of
            // to /article/undefined.
            const rowHref = t.article_id
              ? `/article/${t.article_id}`
              : t.link || "/";
            return (
              <li
                key={`${t.kind}-${t.id}`}
                data-testid={`scheduled-task-row-${t.kind}-${t.id}`}
                className="flex items-start gap-3 px-4 sm:px-5 py-4 hover:bg-neutral-50"
              >
                <div className="mt-0.5 shrink-0">
                  <KindBadge kind={t.kind} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Link
                      to={rowHref}
                      className="font-serif-display text-base leading-snug hover:text-[var(--accent)] block truncate"
                    >
                      {t.article_headline}
                    </Link>
                    {t.recurring && (
                      <span
                        title="Repeats every week — the date shown is the next occurrence"
                        className="text-[9px] uppercase tracking-wider font-mono-display px-1.5 py-0.5 bg-violet-100 text-violet-800"
                      >
                        Repeats
                      </span>
                    )}
                    {t.missed && (
                      <span
                        data-testid={`scheduled-task-missed-${t.id}`}
                        title="WordPress didn't fire on time — reconciler will force-publish it within 15 min"
                        className="text-[9px] uppercase tracking-wider font-mono-display px-1.5 py-0.5 bg-amber-100 text-amber-800"
                      >
                        Missed · auto-fixing
                      </span>
                    )}
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] font-mono-display text-[var(--ink-muted)]">
                    <span className="inline-flex items-center gap-1">
                      <CalendarClock className="w-3 h-3" />
                      {formatWhen(t.scheduled_for, tz)}
                    </span>
                    {formatRelative(t.scheduled_for) && (
                      <span className="text-[var(--ink-muted)]/80">
                        · {formatRelative(t.scheduled_for)}
                      </span>
                    )}
                    <span className="truncate">· {t.target}</span>
                  </div>
                </div>
                <Link
                  to={rowHref}
                  data-testid={`scheduled-task-open-${t.kind}-${t.id}`}
                  className="shrink-0 hidden sm:inline-flex items-center gap-1 text-[11px] px-2 py-1 border border-transparent hover:border-[var(--border)] text-[var(--ink-muted)] hover:text-[var(--ink)]"
                >
                  Open <ChevronRight className="w-3 h-3" />
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    const msg = t.recurring
                      ? `Pause this recurring schedule?\n\n"${t.article_headline}"\n${t.target}\n\nIt stops running but keeps its days, time, and guidelines. Re-enable it on the Research tab.`
                      : `Cancel this ${t.kind} schedule?\n\n"${t.article_headline}"\n${formatWhen(t.scheduled_for, tz)}`;
                    if (window.confirm(msg)) {
                      cancel.mutate({ kind: t.kind, id: t.id });
                    }
                  }}
                  disabled={isPending}
                  title={t.recurring ? "Pause this recurring schedule" : "Cancel this scheduled item"}
                  aria-label={
                    t.recurring
                      ? `Pause recurring schedule ${t.article_headline}`
                      : `Cancel ${t.kind} schedule for ${t.article_headline}`
                  }
                  data-testid={`scheduled-task-cancel-${t.kind}-${t.id}`}
                  className="shrink-0 inline-flex items-center justify-center w-7 h-7 border border-transparent hover:border-red-200 hover:bg-red-50 text-[var(--ink-muted)] hover:text-red-700 disabled:opacity-50"
                >
                  {isPending ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <X className="w-3.5 h-3.5" />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
      </div>
    </div>
  );
}
